import { useState, useRef, useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getListData } from 'api/endpoints/facility';
import Button from 'components/Button';
import LayoutMain from 'components/layouts/LayoutMain';
import { ENUM_ROLE_USER } from 'constants/enum';
import { OPTION_PAGINATION } from 'constants/index';
import FacilityTable from 'pages/TreatmentFacility/List/TreatmentFacilityTable';
import { setModalFacility } from 'store/actions/facility';
import { convertToQueryString } from 'utils/helper';

import CreateOrDetailFacility from './CreateFacility/CreateOrDetailFacility';
import FacilityPagination from './List/TreatmentFacilityPagination';
import FacilityListSearch from './List/TreatmentFacilitySearch';
import OverviewItemsFacility from './List/TreatmentFacilityViewItems';

const TreatmentFacility = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { me } = useSelector((state) => state.auth);
  const { modalFacility } = useSelector((state) => state.facility);

  const [options, setOptions] = useState({ video_category_id: null, ...OPTION_PAGINATION });
  const [sort, setSort] = useState([]);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);

  const isCallingApi = useRef(false);

  const onChangeKeyword = (keyword) => {
    if (keyword !== options.keyword) {
      setOptions((prevState) => {
        return { ...prevState, keyword: keyword };
      });
    }
  };

  // PAGINATION
  const onPageChange = (e) => {
    if (e !== options.page) {
      setOptions((prevState) => {
        return { ...prevState, page: e };
      });
      return true;
    }

    return false;
  };

  // SORT
  const onSortChange = (e) => {
    setSort(() => e);
  };

  /**
   * @name handleSort
   * @returns {string[]}
   * @example ['created_at']
   * @example ['-created_at']
   */
  const handleSort = () => {
    return (
      sort.map((item) => {
        return item.desc ? `-${item.id}` : item.id;
      }) || []
    );
  };

  const handleGetList = async () => {
    if (!isCallingApi.current) {
      isCallingApi.current = true;
      const sortHandle = handleSort();
      const queryString = convertToQueryString({ keyword: options.keyword, sort_by: sortHandle, page: options.page, page_size: options.page_size });
      setLoading(true);
      try {
        await getListData(queryString)
          .then((response) => {
            const totalPage = Math.ceil(response.total / response.page_size);
            const page = response.page > totalPage ? (totalPage === 0 ? 1 : totalPage) : options.page;

            setListData(response?.data || []);
            setOptions((prevState) => {
              return {
                ...prevState,
                total: response.total,
                page_size: response.page_size,
                page: page,
              };
            });
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } catch {
        setLoading(false);
      } finally {
        isCallingApi.current = false;
      }
    }
  };

  // Get list data table
  const getData = useCallback(async () => {
    if (me) {
      handleGetList();
    }
  }, [options.page, options.keyword, sort, me]);

  useEffect(() => {
    getData();
  }, [getData]);


  return (
    <>
      <LayoutMain className={'!max-w-1720'}>
        <section id="question-list">
          <h1 className="text-2xl font-bold text-black/85">{'施設一覧'}</h1>
          <OverviewItemsFacility options={options} />
          <div className="flex items-center justify-between gap-2 mt-2">
            <FacilityListSearch onSubmit={onChangeKeyword} />
            {me?.role === ENUM_ROLE_USER.ADMIN && (<Button
              onClick={() => {
                dispatch(setModalFacility({ isOpen: true, isEdit: false }));
              }}
              color="primary"
              text="施設を追加"
              className="w-[260px] h-9.5"
              icon={<img src={require('assets/resources/Plus.svg').default} alt="plus icon" width={18} height={18} />}
            />)}
          </div>
          <div className={`w-full mt-4 ${options.total > 0 ? 'min-h-164.75' : ''}`}>
            <FacilityTable
              data={listData}
              setSort={onSortChange}
              onPageChange={onPageChange}
              sort={sort}
              page={options.page}
              reloadData={handleGetList}
            />
          </div>
          {options.total > 0 && <FacilityPagination options={options} onPageChange={onPageChange} loading={loading} />}
        </section>
      </LayoutMain>
      {modalFacility.isOpen && <CreateOrDetailFacility
        reloadData={handleGetList}
        onClose={() => dispatch(setModalFacility({ isOpen: false }))}
      />}
    </>
  );
};

export default TreatmentFacility;